// If you have specialized or optional tools, import them as needed:
import QuadraticGraphTool from "../MathTools/QuadraticGraphTool";
import DataAnalyzer from "../MathTools/DataAnalyzer";
import CoordinatePlane from "../MathTools/CoordinatePlane";
import RationalGraphTool from "../MathTools/RationalGraphTool";
import PolygonTool from "../MathTools/PolygonTool";
import { OneIntersection, ZeroIntersection, InfiniteIntersection }
  from "../MathTools/TinyLinePair";
import CoordinateCircle from "../MathTools/CoordinateCircle";
import MiniParabolaPlane from "../MathTools/MiniParabolaPlane";
import NonLinearSystemTool from "../MathTools/NonLinearSystemTool";
import MiniBreakEvenPlane from "../MathTools/MiniBreakEvenPlane";
import ArithmeticGeometricDemo from "../MathTools/ArithmeticGeometriDemo";
import SummationTool from "../MathTools/SummationTool";
import ArithmeticSeriesFlipDemo from "../MathTools/ArithmeticSeriesFlipDemo";
import AnnuityDemo from "../MathTools/AnnuityDemo";
import TelescopingSumDemo from "../MathTools/TelescopingSumDemo";
import DepreciationDemo from "../MathTools/DepreciationDemo";
import CompoundInterestDemo from "../MathTools/CompoundInterestDemo";
import CountingTool from "../MathTools/CountingTool";
import TwoDiceProbDemo from "../MathTools/TwoDiceProbDemo"
import BinomialTool from "../MathTools/BinomialTool";
import NormalDistributionDemo from "../MathTools/NormalDistributionDemo";
import LinearInequalityPlot from "../MathTools/LinearInequalityPlot";
import InequalitySystemPlot from "../MathTools/InequalitySystemPlot";

export const chapterPages = {
    // --------------------------------------------
    // CHAPTER 1: Logical Reasoning & Problem Solving
    // --------------------------------------------
    // CHAPTER 1: Logical Reasoning & Problem Solving (with examples)
  1: [
      {
        title: <h1>Logical Reasoning — Slide 1</h1>,
        body: (
          <>
            <p>
              <strong>Conditional Statements.</strong> A statement of the form “If P then Q” is called a conditional. The “if” part is the hypothesis (P), and the “then” part is the conclusion (Q). We also consider related forms: converse, inverse, and contrapositive.
            </p>
            <ul>
              <li><strong>Example:</strong> If it is raining (P), then the ground is wet (Q).</li>
              <li><strong>Converse:</strong> If the ground is wet, then it is raining. (Not necessarily true.)</li>
              <li><strong>Inverse:</strong> If it is not raining, then the ground is not wet. (Not necessarily true.)</li>
              <li><strong>Contrapositive:</strong> If the ground is not wet, then it is not raining. (Logically equivalent to original.)</li>
            </ul>
          </>
        )
      },
      {
        title: <h1>Logical Reasoning — Slide 2</h1>,
        body: (
          <>
            <p>
              <strong>Counter examples.</strong> To disprove a conditional statement, a single valid counterexample (where the hypothesis is true but the conclusion is false) is sufficient.
            </p>
            <p>
              <strong>Example:</strong> The statement “All birds can fly” is disproved by “Penguins are birds but cannot fly.”
            </p>
          </>
        )
      },
      {
        title: <h1>Logical Reasoning — Slide 3</h1>,
        body: (
          <>
            <p>
              <strong>Problem Solving &amp; Patterns.</strong> Sequences and patterns often require inductive reasoning. We use observed terms to predict the next term or identify a closed form. Always verify if exceptions or alternative interpretations exist.
            </p>
            <p>
              <strong>Example:</strong> Sequence: 2, 4, 8, 16, … Next term is 32. A closed-form formula is a<sub>n</sub> = 2<sup>n</sup>.
            </p>
            <p>
              <strong>Check:</strong> Could there be another rule? Always test additional terms to confirm the pattern.
            </p>
          </>
        )
      },
      {
        title: <h1>Logical Reasoning — Slide 4</h1>,
        body: (
          <>
            <p>
              <em>In this unit, you'll see how logically valid arguments differ from fallacies, and how to test statements or find counterexamples.</em>
            </p>
            <p>
              <strong>Fallacy Example:</strong> Affirming the consequent: “If P then Q. Q is true. Therefore P is true.” This is invalid (e.g., “If it rains then streets are wet. Streets are wet, so it must have rained.”)
            </p>
          </>
        )
      }
    ],
    
  
    // --------------------------------------------
    // CHAPTER 2: Trigonometry
    // --------------------------------------------
  // CHAPTER 2: Trigonometry — Illustrated with MathTools
  2: [
      {
          title: <h1>Trigonometry — Slide 1: Angles in Standard Position</h1>,
          body: (
            <>
              <p>
                <strong>Standard Position:</strong> Vertex at (0,0), initial arm along +x-axis. The <em>terminal arms</em> are shown in red (135°) and blue (−60°).
              </p>
              <CoordinatePlane
                width={400}
                height={400}
                showAxes={true}
                lines={[
                  // initial arm
                  { start: [0, 0], end: [10, 0], color: "black", label: "0°" },
                  // 135° terminal arm
                  {
                    start: [0, 0],
                    end: [
                      10 * Math.cos((135 * Math.PI) / 180),
                      10 * Math.sin((135 * Math.PI) / 180)
                    ],
                    color: "red",
                    label: "135°"
                  },
                  // -60° terminal arm
                  {
                    start: [0, 0],
                    end: [
                      10 * Math.cos((-60 * Math.PI) / 180),
                      10 * Math.sin((-60 * Math.PI) / 180)
                    ],
                    color: "blue",
                    label: "−60°"
                  }
                ]}
                style={{ margin: "1em auto", display: "block" }}
              />
              <ul>
                <li>135° in Q II has reference 45°.</li>
                <li>−60° in Q IV has reference 60°.</li>
              </ul>
            </>
          )
        },
      {
          title: <h1>Trigonometry — Slide 2: Sine Law &amp; Cosine Law</h1>,
          body: (
            <>
              <p>
                In any ΔABC with sides <code>a, b, c</code> opposite <code>A, B, C</code>:
              </p>
              <ul>
                <li>
                  <strong>Sine Law:</strong> <code>a / sin A = b / sin B = c / sin C</code><br/>
                  <em>Use when you know two angles and one side, or two sides and a non-included angle.</em>
                </li>
                <li>
                  <strong>Cosine Law:</strong> <code>c² = a² + b² − 2ab cos C</code><br/>
                  <em>Use when you know three sides or two sides plus the included angle.</em>
                </li>
              </ul>
        
              <div style={{ display: "flex", justifyContent: "space-around", gap: "2rem", margin: "1em 0" }}>
                {/* Sine Law Example */}
                <div style={{ textAlign: "center" }}>
                  <h5>Sine Law Example</h5>
                  <PolygonTool
                    initialPoints={[
                      { x:  50, y: 160 },  // A
                      { x: 250, y: 160 },  // B
                      { x: 130, y:  90 }   // C gives roughly A≈40°, B≈60°
                    ]}
                    labels={["A","B","C"]}
                  />
                  <p>
                    Given A=40°, B=60°, a=10 ➔ <br/>
                    b ≈ (sin 60°/sin 40°)·10 ≈ 12.99
                  </p>
                </div>
        
                {/* Cosine Law Example */}
                <div style={{ textAlign: "center" }}>
                  <h5>Cosine Law Example</h5>
                  <PolygonTool
                    initialPoints={[
                      { x:  50, y: 160 },  // A
                      { x: 200, y: 160 },  // B
                      { x: 140, y:  80 }   // C set so C≈120°
                    ]}
                    labels={["A","B","C"]}
                  />
                  <p>
                    Given a=7, b=5, C=120° ➔<br/>
                    c = √(7²+5²−2·7·5·cos 120°) ≈ 10.44
                  </p>
                </div>
              </div>
            </>
          )
        },
      {
        title: <h1>Trigonometry — Slide 3: Special Angles &amp; Exact Ratios</h1>,
        body: (
          <>
            <p>
              Exact values for key angles:
            </p>
            <table style={{ width: "100%", textAlign: "center", borderCollapse: "collapse" }}>
              <thead>
                <tr><th>θ</th><th>sin θ</th><th>cos θ</th><th>tan θ</th></tr>
              </thead>
              <tbody>
                <tr><td>30°</td><td>½</td><td>√3/2</td><td>√3/3</td></tr>
                <tr><td>45°</td><td>√2/2</td><td>√2/2</td><td>1</td></tr>
                <tr><td>60°</td><td>√3/2</td><td>½</td><td>√3</td></tr>
              </tbody>
            </table>
            <p>ASTC sign pattern: +, +, –, – in QI–QIV.</p>
          </>
        )
      },
      {
          title: <h1>Trigonometry — Slide 4: The SSA “Ambiguous” Case</h1>,
          body: (
            <>
              {/* explanatory text omitted for brevity */}
              <div style={{ display: "flex", justifyContent: "space-around", margin: "1em 0" }}>
                {/* Obtuse‐C solution */}
                <div style={{ textAlign: "center" }}>
                  <PolygonTool
                    initialPoints={[
                      { x:  50, y:  80 },
                      { x: 250, y:  80 },
                      { x: 190, y: 150 }   // C₁ pulled up-high → obtuse at C
                    ]}
                    labels={["A", "B", "C₁"]}
                  />
                  <p>ΔC₁: ∠B = 50° ⟶ ∠C = 100°</p>
                </div>
        
                {/* Acute‐C solution */}
                <div style={{ textAlign: "center" }}>
                  <PolygonTool
                    initialPoints={[
                      { x:  50, y:  80 },
                      { x: 250, y:  80 },
                      { x: 140, y: 130 }   // C₂ pulled lower  → acute at C
                    ]}
                    labels={["A", "B", "C₂"]}
                  />
                  <p>ΔC₂: ∠C = 180° − 100° = 80°</p>
                </div>
              </div>
              <p style={{ textAlign: "center" }}>
                Two triangles satisfy a=6, b=8, A=30°.
              </p>
            </>
          )
        }      
    ],
    
    
  
    // --------------------------------------------
    // CHAPTER 3: Quadratic Functions
    // --------------------------------------------
    3: [
      {
        title: <h1>Quadratic Functions — Slide 1: Standard Form & Parabola</h1>,
        body: (
          <>
            <p>
              A quadratic function is <code>f(x) = ax² + bx + c</code> with <em>a ≠ 0</em>.  
              Its graph is a <strong>parabola</strong> opening up if <code>a &gt; 0</code> or 
              down if <code>a &lt; 0</code>.
            </p>
            <QuadraticGraphTool
              equation="x^2 - 2*x - 3"
              showRoots={true}
              showVertex={true}
              style={{ width: "100%", maxWidth: 500, margin: "1em auto" }}
            />
            <p style={{ textAlign: "center", fontStyle: "italic" }}>
              Here, <code>a=1, b=-2, c=-3</code>: it crosses the x-axis at its two real roots.
            </p>
          </>
        )
      },
    
      {
        title: <h1>Quadratic Functions — Slide 2: Vertex Form & Transformations</h1>,
        body: (
          <>
            <p>
              <strong>Vertex form:</strong> <code>y = a (x - h)² + k</code> makes it easy to
              read off the vertex at <code>(h, k)</code> and see vertical/horizontal shifts.
            </p>
            <QuadraticGraphTool
              equation="2*(x+1)^2 - 4"
              showRoots={false}
              showVertex={true}
              style={{ width: "100%", maxWidth: 500, margin: "1em auto" }}
            />
            <ul>
              <li><code>a=2</code> — steeper than <code>y = x²</code></li>
              <li><code>h = -1</code> — shift left 1</li>
              <li><code>k = -4</code> — shift down 4</li>
            </ul>
          </>
        )
      },
    
      {
        title: <h1>Quadratic Functions — Slide 3: Axis of Symmetry & Discriminant</h1>,
        body: (
          <>
            <p>
              Every parabola has an <strong>axis of symmetry</strong> at 
              <code>x = -b/(2a)</code>.  The <strong>discriminant</strong> 
              <code>Δ = b² - 4ac</code> tells us:
            </p>
            <ul>
              <li><code>Δ &gt; 0</code> → two distinct real roots</li>
              <li><code>Δ = 0</code> → one repeated real root</li>
              <li><code>Δ &lt; 0</code> → no real roots (complex)</li>
            </ul>
            <QuadraticGraphTool
              equation="x^2 + 4*x + 4"
              showRoots={true}
              showVertex={true}
              showAxisOfSymmetry={true}
              style={{ width: "100%", maxWidth: 500, margin: "1em auto" }}
            />
            <p style={{ textAlign: "center", fontStyle: "italic" }}>
              Here <code>a=1,b=4,c=4</code> so <code>Δ=0</code> and the parabola just “touches” the x‐axis at one point.
            </p>
          </>
        )
      },
    
      {
        title: <h1>Quadratic Functions — Slide 4: Completing the Square</h1>,
        body: (
          <>
            <p>
              To convert <code>ax²+bx+c</code> into vertex form, we “complete the square.”  
              <br/>
              <strong>Example:</strong>  <code>x² + 6x + 5</code>
            </p>
            <pre style={{ background: "#f5f5f5", padding: 8, fontFamily: "monospace" }}>
    {`x² + 6x + 5
    = (x² + 6x + 9) - 9 + 5         // add & subtract (b/2)² = 9
    = (x + 3)² - 4                  // vertex form`}
            </pre>
            <QuadraticGraphTool
              equation="(x+3)^2 - 4"
              showRoots={true}
              showVertex={true}
              style={{ width: "100%", maxWidth: 500, margin: "1em auto" }}
            />
            <p style={{ textAlign: "center", fontStyle: "italic" }}>
              Completed square reveals vertex <code>(-3, -4)</code> directly.
            </p>
          </>
        )
      },
      {
          title: <h1>Quadratic Functions — Slide 5: The Quadratic Formula</h1>,
          body: (
            <>
              <p>
                The most general solution of <code>ax² + bx + c = 0</code> is the&nbsp;
                <strong>Quadratic Formula</strong>:
              </p>
        
              {/*–– Formula with fraction bar ––*/}
              <div
                style={{
                  textAlign: "center",
                  margin: "1em 0",
                  fontSize: "1.5em",
                  lineHeight: 1.2
                }}
              >
                <span style={{ verticalAlign: "middle", marginRight: ".3em" }}>x =</span>
                <span
                  style={{
                    display: "inline-block",
                    textAlign: "center",
                    verticalAlign: "middle"
                  }}
                >
                  {/* Numerator */}
                  <span
                    style={{
                      display: "block",
                      borderBottom: "2px solid #333",
                      padding: ".2em .5em"
                    }}
                  >
                    −b ± √(b² − 4ac)
                  </span>
                  {/* Denominator */}
                  <span style={{ display: "block", padding: ".2em .5em" }}>
                    2a
                  </span>
                </span>
              </div>
        
              <p>
                <strong>Derivation sketch:</strong> start from <code>ax² + bx + c = 0</code>, divide
                by <code>a</code>, then complete the square on <code>x² + (b/a)x</code>.
              </p>
        
              {/*–– Worked example in column form ––*/}
              <div
                style={{
                  textAlign: "center",
                  fontFamily: "monospace",
                  fontSize: "1.1em",
                  lineHeight: "1.4em",
                  margin: "1.5em 0"
                }}
              >
                {/* each step on its own line */}
                x = [4 ± √64] / (2·2)  
                <br />
                x = (4 ± 8) / 4  
                <br />
                x = 3 or −1
              </div>
        
              <p>
                And that gives the two solutions for <code>2x² − 4x − 6 = 0</code> as <strong>3</strong> and
                <strong> −1</strong>.
              </p>
            </>
          )
        },
        {
          title: <h1>Quadratic Functions — Slide 6: Applications & Optimization</h1>,
          body: (
            <>
              {/* shrunk paragraph */}
              <div style={{
                fontSize: "0.9em",
                maxWidth: "600px",
                margin: "0 auto",
                lineHeight: 1.4
              }}>
                <p>
                  In business we often model profit with a quadratic. For example:{" "}
                  <code style={{ fontSize: "0.95em" }}>
                    P(x) = −2x² + 80x − 300
                  </code>, where <code>x</code> is units sold and <code>P(x)</code> is profit in dollars.
                </p>
              </div>
        
              {/* full parabola, 1 grid unit = \$100 */}
              <QuadraticGraphTool
                equation="(-2*x*x + 80*x - 300)/100"
                showVertex={true}
                showAxis={true}
                widthCells={50}      // x from −25 to +25
                heightCells={12}     // y from −6 to +6
                cellSize={30}
                style={{ width: "100%", maxWidth: 600, margin: "1em auto" }}
              />
              <p style={{ textAlign: "center", fontStyle: "italic", fontSize: "0.9em" }}>
                Here we’ve plotted <code>P(x)/100</code>, so the vertex at <code>(20, 5)</code> really means <strong>(20, \$500)</strong>.
              </p>
        
              {/* question prompt */}
              <p style={{ fontSize: "1em", fontWeight: "bold", textAlign: "center" }}>
                Question: How many units should we sell to break even? (Find the roots of <code>P(x)=0</code>.)
              </p>
        
              {/* mini “roots only” view */}
              <QuadraticGraphTool
                equation="(-2*x*x + 80*x - 300)/100"
                showRoots={true}
                showVertex={false}
                showAxis={true}
                widthCells={50}
                heightCells={8}     // just enough vertical room
                cellSize={20}
                style={{ width: "100%", maxWidth: 500, margin: "1em auto" }}
              />
              <p style={{ textAlign: "center", fontStyle: "italic", fontSize: "0.9em" }}>
                The blue dots are the break-even points at approximately <code>x ≈ 4.2</code> and <code>x ≈ 35.8</code> units.
              </p>
            </>
          )
        }         
    ],
  
    // --------------------------------------------
    // CHAPTER 4: Rational Expressions & Equations
    // --------------------------------------------
    4: [
      // ----------------------------------------------------------
      // SLIDE 1: Introduction and Examples
      // ----------------------------------------------------------
      {
        title: <h1>Rational Expressions — Slide 1: Introduction</h1>,
        body: (
          <>
            <p>
              A <strong>rational expression</strong> is a fraction in which the numerator or
              denominator (or both) is a polynomial. We can write it in the form{" "}
              <code>p(x)/q(x)</code> where <code>p</code> and <code>q</code> are polynomials,
              and <code>q(x) ≠ 0</code>.
            </p>
            <div style={{ margin: "1em 0" }}>
              <h4>Examples of Rational Expressions:</h4>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <tbody>
                  <tr>
                    <td style={{ width: "50%", textAlign: "center", padding: "0.5em" }}>
                      <code>(x^2 - 9)/(x - 3)</code>
                    </td>
                    <td style={{ width: "50%", textAlign: "center", padding: "0.5em" }}>
                      <code>(2x + 1)/(x^2 + 4x + 4)</code>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p>
              Rational expressions arise in many real‐world settings, from rates and proportions
              (speed = distance/time) to more complex models in physics, economics, or biology.
            </p>
          </>
        )
      },
    
      // ----------------------------------------------------------
      // SLIDE 2: Non-Permissible Values (Domain Restrictions)
      // ----------------------------------------------------------
      {
        title: <h1>Rational Expressions — Slide 2: Domain & Non-Permissible Values</h1>,
        body: (
          <>
            <p>
              We must exclude <em>non-permissible values</em> from the domain—these are
              values of <code>x</code> that make the denominator zero (since division by zero
              is undefined).
            </p>
            <ul>
              <li>
                For <code>(x^2 - 9) / (x - 3)</code>, the denominator <code>x - 3</code> is zero
                when <code>x = 3</code>. So, <code>x = 3</code> is excluded.
              </li>
              <li>
                For <code>(2x + 1) / (x^2 + 4x + 4)</code>, note that{" "}
                <code>x^2 + 4x + 4 = (x + 2)^2</code> is zero at <code>x = -2</code>. 
                So, <code>x = -2</code> is excluded.
              </li>
            </ul>
            <p>
              Always identify these restricted <code>x</code>‐values first before simplifying 
              or solving a rational expression or equation.
            </p>
          </>
        )
      },
    
      // ----------------------------------------------------------
      // SLIDE 3: Simplifying by Factoring
      // ----------------------------------------------------------
      {
        title: <h1>Rational Expressions — Slide 3: Simplifying by Factoring</h1>,
        body: (
          <>
            <p>
              To <strong>simplify</strong> a rational expression, we often <em>factor</em> the
              numerator and the denominator, then cancel any common factors (provided they’re
              not zero).
            </p>
            <p>
              <strong>Example:</strong> Simplify <code>(x^2 - 9) / (x - 3)</code>.
            </p>
            <pre style={{ background: "#f5f5f5", padding: 10 }}>
  {`x^2 - 9 = (x - 3)(x + 3)
  So, (x^2 - 9)/(x - 3) = (x + 3), (x ≠ 3)`}
  </pre>
            <p>
              As long as <code>x ≠ 3</code>, we can cancel <code>(x - 3)</code>, yielding
              <code>x + 3</code>. But remember, <code>x = 3</code> is excluded from the domain.
            </p>
          </>
        )
      },
    
      // ----------------------------------------------------------
      // SLIDE 4: Partial Fractions
      // ----------------------------------------------------------
      {
          title: <h1>Rational Expressions — Slide 4: Partial Fraction Decomposition</h1>,
          body: (
            <>
              <p>
                <strong>Partial fractions</strong> is a technique to rewrite a complicated fraction
                into a sum of simpler fractions. 
              </p>
              <p>
                <strong>Example:</strong> Decompose <code>(2x + 1)/(x^2 + 3x + 2)</code>.
                First factor the denominator: <code>x^2 + 3x + 2 = (x+1)(x+2)</code>.
              </p>
              <pre style={{ background: "#f5f5f5", padding: 10 }}>
        {`(2x + 1) / ((x+1)(x+2)) = A/(x+1) + B/(x+2).`}
              </pre>
              <p>
                Solve for <code>A</code> and <code>B</code> by combining over a common denominator 
                or by substituting convenient values of <code>x</code>. 
                Often, partial fractions appear in integration, advanced algebra, and rational 
                function graphing.
              </p>
            </>
          )
        },
        
        // ----------------------------------------------------------
        // SLIDE 5: Solving Rational Equations
        // ----------------------------------------------------------
        {
          title: <h1>Rational Expressions — Slide 5: Solving Rational Equations</h1>,
          body: (
            <>
              <p>
                A <strong>rational equation</strong> is an equation that has one or more rational
                expressions. A common strategy is to multiply both sides by a common denominator
                to clear the fractions. 
              </p>
              <p>
                <strong>Watch out for extraneous solutions!</strong> Sometimes multiplying 
                by a variable expression introduces “solutions” that make the original denominator
                zero. Always check your final answers in the original equation.
              </p>
              <pre style={{ background: "#f5f5f5", padding: 10 }}>
        {`Example: Solve x/(x-2) = 3.
        
        Multiply both sides by (x-2):
        x = 3(x - 2)
        x = 3x - 6
        -2x = -6
        x = 3
        
        Check x=3: denominator (3-2)=1 (not zero), valid solution!
        `}
              </pre>
            </>
          )
        },
        
    
      // ----------------------------------------------------------
      // SLIDE 6: Graphing Rational Functions
      // ----------------------------------------------------------
      {
          title: <h1>Rational Expressions — Slide 6: Graphing & Asymptotes</h1>,
          body: (
            <>
              <p>When graphing rational functions <code>f(x) = p(x) / q(x)</code>, we look for:</p>
              <ul>
                <li>Vertical asymptotes at values of x making <code>q(x) = 0</code>.</li>
                <li>Horizontal or oblique asymptotes based on degrees of p(x) vs. q(x).</li>
                <li>Holes if a common factor cancels in numerator and denominator.</li>
              </ul>
              <p>
                Here’s an example: <code>y = 1/(x - 2)</code> has a vertical asymptote at <code>x=2</code>.
              </p>
        
              {/* Render the new tool without breaking older code */}
              <RationalGraphTool
                equation="1/(x-2)"
                width={400}
                height={300}
                xRange={[-6, 6]}
                yRange={[-6, 6]}
              />
        
              <p>
                Carefully identify domain restrictions, intercepts, and asymptotes to sketch or interpret
                rational function graphs.
              </p>
            </>
          )
        }
    ],
    
  
    // --------------------------------------------
    // CHAPTER 5: Systems of Equations
    // --------------------------------------------
    5: [
      // SLIDE 1 – Review of solution methods
      {
        title: <h1>Systems of Equations — Slide 1: Three Core Methods</h1>,
        body: (
          <>
            <ul>
              <li><strong>Graphing.</strong> Plot each curve; intersections are solutions.</li>
              <li><strong>Substitution.</strong> Solve one equation for a variable and plug in.</li>
              <li><strong>Elimination.</strong> Add / subtract multiples to remove one variable.</li>
            </ul>
            <p style={{fontSize:"1.4em"}}>
              In Grade 11 we extend these methods to <em>non-linear</em> systems
              such as <code>y = 2x + 3</code> with <code>y = x<sup>2</sup></code>.
            </p>
          </>
        )
      },
    
      // SLIDE 2 – Linear systems: 0-1-∞ outcomes (with mini-graph)
      {
          title: <h1>Systems of Equations — Slide 2: Linear × Linear Outcomes</h1>,
          body: (
            <>
  
              <p style={{textAlign:"center",marginBottom:8}}>
                <strong>Number&nbsp;of&nbsp;Solutions for Two Lines</strong>
              </p>
                <div className="line-model-row">
    <div className="line-model-card">
      <h1>One intersection</h1>
      <div className="diagram-container">
        <OneIntersection />
      </div>
    </div>
    <div className="line-model-card">
      <h1>None (parallel)</h1>
      <div className="diagram-container">
        <ZeroIntersection />
      </div>
    </div>
    <div className="line-model-card">
      <h1>Infinitely many</h1>
      <div className="diagram-container">
        <InfiniteIntersection />
      </div>
    </div>
  </div>
              <p style={{fontSize:"1.2em"}}>
                <strong>Slope test.</strong>&nbsp;
                Different slopes ⇒ exactly 1 intersection;&nbsp;
                same slope with different intercepts ⇒ 0;&nbsp;
                identical equations ⇒ infinitely many.
              </p>
            </>
          )
        },
    
        {
          title: <h1>Systems of Equations — Slide 3: Line + Parabola, Circle + Line …</h1>,
          body: (
            <>
              <p>
                When we move beyond two linear equations, more interesting situations arise:
              </p>
              <ul>
                <li>
                  A <strong>line and a parabola</strong> can have <strong>0, 1, or 2</strong> intersection points, depending on how the line crosses (or touches, or misses) the parabola.
                </li>
                <li>
                  A <strong>circle and a line</strong> can similarly have <strong>0, 1 (tangent), or 2</strong> intersection points.
                </li>
              </ul>
        
              <p>
                We typically solve these via <em>substitution</em> (plug the linear expression for \(y\) into the quadratic or circle equation) or by using graphing technology to see where they intersect. In higher courses, you'll also see these ideas extended to more complicated curves.
              </p>
        
              {/* A small two‐column layout to show examples side by side (line+parabola and line+circle) */}
              <div style={{ display: "flex", gap: "2rem", justifyContent: "center", flexWrap: "wrap" }}>
        
                {/* 1) Line + Parabola example */}
                <div style={{ border: "1px solid #ccc", padding: "1rem", borderRadius: "8px", minWidth: "220px" }}>
                  <h4>Line + Parabola</h4>
                  <p style={{ fontSize: "0.9em", marginTop: 0 }}>
                    e.g. <code>y = 2x - 3</code> and <code>y = x<sup>2</sup></code>
                  </p>
                  {/* A small coordinate plane if you want to use your tool: */}
                  <CoordinatePlane
                    width={300}
                    height={300}
                    showAxes={true}
                    disablePlot={true}
                    lines={[
                      // parabola => you could pass it as many small segments or skip if your tool doesn't do curves
                      // For demonstration, here's just a partial approach; or omit if you can't easily draw curves.
                      { start: [-2, 4], end: [-1, 1], color: "gray" },
                      { start: [-1, 1], end: [0, 0], color: "gray" },
                      { start: [0, 0], end: [1, 1], color: "gray" },
                      { start: [1, 1], end: [2, 4], color: "gray" },
                      
                      // line y=2x-3 => from x=-2..2 for example
                      { start: [-2, -7], end: [2, 1], color: "blue", label: "y=2x-3" }
                    ]}
                    // You could omit lines for the parabola if your tool doesn't do arcs,
                    // or find a workaround to "fake" it with small segments. 
                  />
                  <p style={{ fontSize: "0.8em", marginTop: "0.5rem" }}>
                    Substituting gives <code>2x - 3 = x<sup>2</sup></code>. Solve for x to find up to 2 real solutions.
                  </p>
                </div>
        
                {/* 2) Line + Circle example */}
                <div style={{ border: "1px solid #ccc", padding: "1rem", borderRadius: "8px", minWidth: "220px" }}>
                  <h4>Circle + Line</h4>
                  <p style={{ fontSize: "0.9em", marginTop: 0 }}>
                    e.g. <code>x<sup>2</sup> + y<sup>2</sup> = 16</code> and <code>y = 2</code>
                  </p>
                  <CoordinateCircle
          width={300}
          height={300}
          circleRadius={5}
          circleColor="blue"
          showAxes={true}
          disablePlot={true} 
          lines={[{ start: [-6,2], end: [6,2], color: "red", label: "y=2" }]}/>
                  <p style={{ fontSize: "0.8em", marginTop: "0.5rem" }}>
                    A circle <code>(x<sup>2</sup> + y<sup>2</sup> = 16)</code> can intersect line <code>y=2</code> in 2 points, 1 point (tangent), or 0 points, depending on position.
                  </p>
                </div>
              </div>
              <p style={{ fontSize: "1.2em", marginTop: "1rem" }}>
                So, “non‐linear” systems often require factoring or the Quadratic Formula for solutions. We rely on substitution or graphing to find where they intersect.
              </p>
            </>
          )
        },      
    
      // SLIDE 4 – Worked algebraic example (substitution)
      {
          title: <h1>Systems of Equations — Slide 4: Worked Example (Substitution)</h1>,
          body: (
            <>
              <p>
                Consider the system:
              </p>
              <pre style={{ background: "#f5f5f5", padding: 10, overflowX: "auto" }}>
                {`Solve  { y = 2x + 1
                    { y = x^2 - 3x + 4
        
          Substitute → 2x + 1 = x^2 - 3x + 4
                        0 = x^2 - 5x + 3
          Use quadratic formula:
              x = [5 ± √(25 - 12)] / 2
                = (5 ± √13)/2
        
          Back-substitute to get y.`}
              </pre>
        
              <p>x = [5 ± √(25 - 12)] &#47; 2 = (5 ± √13)&#47;2</p>
  
        
              {/* Add a small coordinate-plane to visualize the line & parabola */}
              <div style={{ display: "flex", justifyContent: "center", margin: "1rem 0" }}>
                <div style={{ border: "1px solid #ccc", padding: "1rem", borderRadius: "6px" }}>
                  <h4>Visual Check:</h4>
                  <MiniParabolaPlane />
                </div>
              </div>
        
              <p style={{ fontSize: "0.9em", marginTop: "0.5rem" }}>
                By graphing, we see the line <code>y=2x+1</code> crossing the parabola <code>y=x<sup>2</sup> -3x+4</code> at exactly two points, confirming the algebraic result.
              </p>
            </>
          )
        },
    
      // SLIDE 5 – Interactive tool
      {
        title: <h1>Systems of Equations — Slide 5: Try It Yourself</h1>,
        body: (
          <>
            <p>
              <em>Drag, edit, or add equations in the&nbsp;
              <strong>Non-Linear System SolverTool</strong> below. Watch how the
              intersection points update.</em>
            </p>
            <NonLinearSystemTool />
          </>
        )
      },
    
      {
          title: <h1>Systems of Equations — Slide 6: Real-World Example</h1>,
          body: (
            <>
              <p>
                A company’s <strong>revenue</strong> is modelled by the line{" "}
                <code>R(x) = 25x</code> dollars, while its <strong>cost</strong> (including
                a quadratic “efficiency” term) is <code>C(x) = 10x + 0.1x<sup>2</sup> + 200</code>.
              </p>
              <p>
                The <strong>break‐even points</strong> occur where <code>R(x) = C(x)</code>
                &nbsp;— so it’s a line + parabola system that can have up to two solutions.
              </p>
        
              {/* The algebraic solution */}
              <pre style={{ background:"#f5f5f5", padding:10, overflowX:"auto" }}>
        {`25x = 10x + 0.1x^2 + 200
        0 = 0.1x^2 - 15x + 200
        x ≈ 10.7 or x ≈ 186.6  (units)`}
              </pre>
        
              <p style={{ margin: "0.4em 0" }}>
                Typically, <em>both</em> are valid solutions to the equation, but in real‐world
                terms, the company might only produce up to around 100 units, so that second
                “solution” at <strong>x ≈ 186.6</strong> is beyond capacity.
                <br/>
                Hence the realistic break‐even is at about <strong>10.7&nbsp;units</strong>.
              </p>
        
              {/* Visual approach with a small plane: */}
              <div style={{ display: "flex", gap: "2rem", alignItems: "flex-start" }}>
                <div>
                  <h4>Visual Check:</h4>
                  <MiniBreakEvenPlane />
                </div>
                <div style={{ fontSize: "0.9em" }}>
                  <p>
                    By graphing the <strong>revenue line</strong> and <strong>cost parabola</strong>,
                    we see <code>x≈10.7</code> as the smaller intersection, representing the feasible
                    break‐even quantity.
                  </p>
                  <p style={{ fontStyle:"italic" }}>
                    The larger intersection near <code>x≈186.6</code> also solves the equation
                    mathematically, but might be unrealistic if the company can’t produce that high.
                  </p>
                </div>
              </div>
            </>
          )
        }      
    ],
  
    // --------------------------------------------
    // CHAPTER 6: Sequences & Series
    // --------------------------------------------
    6: [
      {
        title: <h1>Sequences &amp; Series — Slide 1: Advanced Review & Sigma Notation</h1>,
        body: (
          <>
            <p>
              In Grade 10, we learned how arithmetic and geometric sequences are built. Now, 
              let’s formalize them using <strong>summation (sigma) notation</strong> and 
              explore more complex patterns.
            </p>
            <p>
              <strong>Example (Arithmetic):</strong> 
              We can write the sum of the first <code>n</code> terms of an arithmetic sequence
              as:
            </p>
            <pre style={{ background: "#f5f5f5", padding: 8 }}>
              S<sub>n</sub> = ∑ (a<sub>1</sub> + (k−1)d), from k=1 to n
            </pre>
            <p>
              Likewise, a geometric series can be expressed in sigma notation as:
            </p>
            <pre style={{ background: "#f5f5f5", padding: 8 }}>
              S<sub>n</sub> = ∑ (a<sub>1</sub> r<sup>k−1</sup>), from k=1 to n
            </pre>
            <p>
              Using sigma notation allows us to handle more complicated sequences 
              (like piecewise or multi-step definitions) and to set up equations 
              that help solve real-world problems more systematically.  
            </p>
               <SummationTool 
                 expression="a1 + (k-1)*d" 
                 startIndex={1} 
                 endIndex={5}
                 showGraph={true}  // <--- enables the side-by-side graph
               />
  
          </>
        )
      },
      {
        title: <h1>Sequences &amp; Series — Slide 2: Deriving the Sum Formulas</h1>,
        body: (
          <>
            <p>
              We already know the “shortcuts” for summing arithmetic or geometric series, 
              but let’s see an **advanced derivation** approach or a deeper insight 
              into why these formulas hold.
            </p>
            <ul>
              <li>
                <strong>Arithmetic Series</strong>: 
                <code>S<sub>n</sub> = (n/2) [2a<sub>1</sub> + (n−1)d]</code>. 
                <br />
                <em>Derivation Idea:</em> Write the series forward and backward, then add term-by-term.
              </li>
              <li>
                <strong>Geometric Series</strong>:
                <code>S<sub>n</sub> = a<sub>1</sub> (r<sup>n</sup> − 1) / (r − 1)</code> 
                if <code>r ≠ 1</code>. 
                <br />
                <em>Derivation Idea:</em> Multiply the sum by <code>r</code>, subtract from the original, 
                and isolate <code>S<sub>n</sub></code>.
              </li>
            </ul>
            <p>
              <strong>Advanced Note:</strong> For <code>|r| &lt; 1</code>, the infinite sum 
              converges: <code>S<sub>∞</sub> = a<sub>1</sub> / (1 − r)</code>. 
              In Grade 11, you can apply this to financial math (like perpetuities) 
              and certain repeating decimal expansions.
            </p>
            <p>
          <strong>Visual Explanation (Flip Method):</strong>
        </p>
        <ArithmeticSeriesFlipDemo
          a1={3}
          d={2}
          n={5}
          width={450}
          height={220}
        />
        <p>
          This demonstration shows the top row in “forward” order, the bottom row in
          “reverse.” Each column adds to the same total of (a<sub>1</sub> + a<sub>n</sub>).
        </p>
          </>
        )
      },
      {
        title: <h1>Sequences &amp; Series — Slide 3: Applications &amp; Problem-Solving</h1>,
        body: (
          <>
            <p>
              <strong>1) Finance Example:</strong> Suppose you deposit \$200 every month 
              into a savings account that yields 1% monthly interest. This is effectively 
              a <em>geometric-like</em> growth each deposit:
            </p>
            <ul>
              <li>Each deposit is an additional term in a growing sum (an annuity).</li>
              <li>The total amount after <code>n</code> months can be treated as the sum 
                of a geometric progression with ratio <code>(1 + 0.01)</code>.
              </li>
            </ul>
      
            <AnnuityDemo
              monthlyDeposit={200}
              monthlyRate={0.01}
              months={12}
              width={2000}
              height={300}
            />
            <p>
              <strong>2) “Weird” Sequences:</strong> We can define a sequence with 
              arithmetic behavior in one interval and geometric in another. For example:
            </p>
            <pre style={{ background: "#f5f5f5", padding: "8px" }}>
      {`aₙ = {
        2n + 1,       for n ≤ 5
        3 × a₍n-1₎,   for n > 5
      }`}
            </pre>
            <p>
              By piecewise definition, the first five terms are arithmetic, but after 
              that they switch to a geometric recurrence. Understanding both arithmetic 
              and geometric ideas lets us handle these mixed scenarios.
            </p>
      
            <p>
              <strong>3) Series in Algebraic Proofs:</strong> Beyond these, many common
              algebraic identities use “sum of terms” logic. Some brief examples:
            </p>
            <ul>
              <li>
                <strong>Sum of the First n Integers:</strong>{" "}
                <code>1 + 2 + 3 + ... + n = n(n+1)/2</code>.
                <br />
                This can be shown by pairing terms from the start and end.
              </li>
              <li>
                <strong>Sum of Squares:</strong>{" "}
                <code>1² + 2² + ... + n² = n(n+1)(2n+1)/6</code>. 
                <br />
                A short inductive or telescoping proof handles this finite series.
              </li>
              <li>
                <strong>Binomial Squares/Cubes:</strong>{" "}
                <code>(a + b)² = a² + 2ab + b²</code>, etc.
                <br />
                Each term in the expansion can be seen as part of a finite sum.
              </li>
            </ul>
            <p>
              In higher math or early calculus, such ideas generalize to 
              <em>infinite</em> expansions (like power series, binomial expansions, etc.),
              but even at Grade 11, the logic of summation underpins many 
              key algebraic results and proofs.
            </p>
          </>
        )
      },
      {
        title: <h1>Sequences &amp; Series — Slide 4: Telescoping Sums</h1>,
        body: (
          <>
            <p>
              Another advanced topic is the <strong>telescoping series</strong>, where many terms 
              cancel out pairwise...
            </p>
            <pre style={{ background: "#f5f5f5", padding: 8 }}>
      {`1/(1·2) + 1/(2·3) + 1/(3·4) + ... + 1/[n(n+1)]`}
            </pre>
            <p>
              Notice how <code>1/(k(k+1))</code> can be decomposed...
            </p>
            <TelescopingSumDemo maxN={6} />
            <p>
              Telescoping ideas appear in advanced summations...
            </p>
          </>
        )
      },
        {
          title: <h1>Sequences &amp; Series — Slide 5: Arithmetic–Geometric Combinations</h1>,
          body: (
            <>
              <p>
                Occasionally, you’ll see a sequence that’s <em>partly</em> arithmetic, 
                yet each term also has a factor that grows (or shrinks) geometrically—called 
                an <strong>arithmetic–geometric sequence</strong>. 
              </p>
              <p>
                For instance: <code>a<sub>n</sub> = n·2<sup>n</sup></code>. Here, <code>n</code> grows linearly, but 
                <code>2<sup>n</sup></code> grows exponentially. Summing <code>n·2<sup>n</sup></code> from <code>n=1</code> 
                to <code>N</code> often requires a combination of techniques (like repeated application 
                of the geometric sum formula plus an extra step to account for the <code>n</code> factor).
              </p>
        
              <p>
                <strong>Illustration:</strong> Below is a plot of the first 6 terms of 
                a<sub>n</sub> = n·2<sup>n</sup> (<em>i.e.</em>, (1,2), (2,8), (3,24), etc.). Notice how the 
                y-values grow faster and faster:
              </p>
        
              {/* Example approach using <CoordinatePlane> to show discrete points */}
              <ArithmeticGeometricDemo
              disablePlot={true} />
              
              <p style={{ marginTop: "1em" }}>
                <strong>Real Example:</strong> An interest deposit that increases by an extra \$10 each 
                month (arithmetic increment) while also compounding monthly (geometric). 
                Handling such cases requires carefully blending the arithmetic and geometric formulas 
                or a recurrence-based approach.
              </p>
            </>
          )
        }
    ],
    
    
  
    // --------------------------------------------
    // CHAPTER 7: Financial Literacy
    // --------------------------------------------
    7: [
      // 7: [
  {
    title: <h1>Financial Literacy — Slide 1: Compound Interest</h1>,
    body: (
      <>
        <p>
          <strong>Compound Interest (Lump Sum):</strong> 
          If you invest a principal <em>P</em> at an annual interest rate <em>r</em>
          (compounded once per year), after <em>t</em> full years:
          <br/>
          <code>A = P (1 + r)<sup>t</sup></code>.
        </p>
        <p>
          This formula shows exponential growth—each year multiplies your current total 
          by <em>(1 + r)</em>. 
        </p>
  
        {/* A small tool to plot the value each year (like 0..t) */}
        <CompoundInterestDemo
          principal={1000}
          rate={0.05}
          years={10}
          width={1800}
          height={300}
        />
  
        <p style={{ marginTop: "1em" }}>
          In practice, compounding frequency may be quarterly or monthly instead of annual, 
          adjusting the formula to <em>A = P (1 + r/m)<sup>m·t</sup></em>, 
          where <code>m</code> is compounding periods per year.
        </p>
      </>
    )
  },
  {
    title: <h1>Financial Literacy — Slide 2: Annuities</h1>,
    body: (
      <>
        <p>
          <strong>Annuities (Regular Deposits):</strong> 
          When you deposit (or withdraw) a regular amount, each contribution 
          can earn interest over time. The sum acts like a
          <em> geometric‐type series</em>. 
        </p>
        <p>
          If you deposit <em>D</em> at the end of each period, with interest rate <em>r</em> 
          per period, after <em>n</em> deposits:
          <br/>
          <code>S(n) = D × [(1 + r)<sup>n</sup> − 1] / r</code>.
        </p>
  
        {/* Reuse the existing AnnuityDemo to show monthly deposit scenario */}
        <AnnuityDemo
          monthlyDeposit={200}
          monthlyRate={0.01}   // 1% monthly
          months={12}
          width={2200}
          height={300}
        />
  
        <p style={{ marginTop: "1em" }}>
          This “annuity formula” is common for retirement savings, mortgages, and more. 
          Different compounding vs. deposit frequencies can complicate the exact formula, 
          but the principle is similar: each payment forms part of a growing sum.
        </p>
      </>
    )
  },
  {
    title: <h1>Financial Literacy — Slide 3: Depreciation</h1>,
    body: (
      <>
        <p>
          <strong>Depreciation (Negative Growth):</strong> 
          In many real‐world situations (like car value, technology hardware), 
          an item <em>loses</em> value each year at some rate <em>d</em>. 
          After 1 year at 10% depreciation, you multiply by (1−0.10)=0.90. 
          After <em>t</em> years:
          <br/>
          <code>Value<sub>t</sub> = Value<sub>0</sub> × (1 − d)<sup>t</sup></code>.
        </p>
  
        {/* A small tool to visualize that decay over several years */}
        <DepreciationDemo
          initialValue={20000}
          depreciationRate={0.10}  // 10% per year
          years={5}
          width={1850}
          height={300}
        />
  
        <p style={{ marginTop: "1em" }}>
          This acts like compound interest in reverse—each year the asset multiplies 
          by a factor less than 1, causing exponential <em>decay</em> rather than growth.
        </p>
      </>
    )
  }
    ],
  
    // --------------------------------------------
    // CHAPTER 8: Statistics & Probability
    // --------------------------------------------
    8: [
      // SLIDE 1: Measures of Central Tendency & Variation
      {
        title: <h1>Statistics &amp; Probability — Slide 1: Measures of Central Tendency &amp; Variation</h1>,
        body: (
          <>
            <p>
              <strong>Mean, Median, Mode.</strong> We often use these three to describe the “center” of data:
            </p>
            <ul>
              <li><strong>Mean</strong> (average): Very sensitive to outliers.</li>
              <li><strong>Median</strong>: Middle value in sorted data; robust to outliers.</li>
              <li><strong>Mode</strong>: Most frequent value—useful for “most common” category.</li>
            </ul>
    
            <p>
              <strong>Variation:</strong> Two datasets can share the same mean yet have drastically different spreads:
            </p>
            <ul>
              <li><strong>Range</strong>: max − min.</li>
              <li><strong>Interquartile Range (IQR)</strong>: Q3 − Q1 (middle 50% of data).</li>
              <li><strong>Standard Deviation</strong>: a common measure indicating how far data typically is from the mean.</li>
            </ul>
    
            <p>
              <strong>Example:</strong> <code>[3, 3, 4, 5, 100]</code> has mean ~23 (pulled up by 100),
              median=4, mode=3, range=97, large std. dev. due to the outlier 100.
            </p>
    
          <DataAnalyzer
            initialData={[3,3,4,5,100]}
            showMean
            showMedian
            showMode
            showStdDev
            showBoxPlot
          />
    
   
            <p style={{ marginTop: "1em" }}>
              By combining central tendency (mean/median) with spread (range/IQR/std dev), 
              we get a fuller picture of the data’s distribution and potential outliers.
            </p>
          </>
        )
      },
    
      // SLIDE 2: Probability Fundamentals & Counting
      {
        title: <h1>Statistics &amp; Probability — Slide 2: Probability Fundamentals &amp; Counting</h1>,
        body: (
          <>
            <p><strong>Basic Probability.</strong> If outcomes are equally likely:</p>
            <div style={{ paddingLeft: 15 }}>
              <code>Probability = (# favorable) / (# total)</code>
            </div>
            <p>
              <strong>Example:</strong> A fair die → P(3)=<code>1/6</code>, a fair coin → P(Heads)=<code>1/2</code>.
            </p>
            <p>
              For multi-stage or non-uniform events, we often use <strong>permutations</strong> &amp; <strong>combinations</strong> to count possibilities:
            </p>
            <ul>
              <li><strong>Permutation (ordered)</strong>: # ways to arrange <em>n</em> distinct items into <em>k</em> slots (<code>nPk</code>).</li>
              <li><strong>Combination (unordered)</strong>: # ways to choose <em>k</em> items from <em>n</em> (<code>nCk</code>).</li>
            </ul>
    
            <CountingTool showPermutations showCombinations />
    
            {/* Or a small ASCII tree diagram for permutations vs. combinations */}
            <pre style={{ background:"#f5f5f5", padding:6 }}>
    {`Permutation example: 
      For 3 distinct letters {A,B,C}, 
      ways to arrange 2 = 3P2=6:
        AB AC BA BC CA CB
    `}
            </pre>
    
            <p style={{ marginTop: "1em" }}>
              Counting methods let us handle more complex probabilities (e.g. dealing 5 cards from 52).
            </p>
          </>
        )
      },
    
      // SLIDE 3: Compound Events & Independence
      {
        title: <h1>Statistics &amp; Probability — Slide 3: Compound Events &amp; Independence</h1>,
        body: (
          <>
            <p>
              <strong>Compound Events:</strong> e.g. rolling two dice → <code>6&times;6=36</code> outcomes. Probability(sum=7)=<code>6/36=1/6</code>.
            </p>
            <p>
              <strong>Independent Events:</strong> If the outcome of one doesn’t affect the other, then P(A &amp; B)=P(A)×P(B).
            </p>
            <p>
              <strong>Conditional Probability:</strong> P(A | B)=P(A &amp; B)/P(B). If not independent (e.g. drawing cards without replacement), probabilities shift each draw.
            </p>
            <p>
              <strong>Mutually Exclusive:</strong> If events can’t both occur, P(A &amp; B)=0, so P(A or B)=P(A)+P(B).
            </p>
    
            {/* If you have a TwoDiceProbDemo: */}
            <TwoDiceProbDemo showDistribution showSumTable />
    
            {/* Or ASCII 6x6 grid of dice outcomes */}
            <pre style={{ background:"#f5f5f5", padding:6 }}>
    {`Dice outcomes (first die=rows, second=cols):
       (1,1) (1,2) ... (1,6)
       (2,1) (2,2) ... (2,6)
         ...
       (6,1) (6,2) ... (6,6)
       => 36 equally likely outcomes
    `}
            </pre>
    
            <p style={{ marginTop: "1em" }}>
              These rules are vital for real tasks (insurance, genetics, multi-step finance) 
              and analyzing overlapping vs. disjoint events.
            </p>
          </>
        )
      },
    
      // SLIDE 4: Binomial & Other Distributions
      {
        title: <h1>Statistics &amp; Probability — Slide 4: Binomial &amp; Other Distributions</h1>,
        body: (
          <>
            <p>
              Repeated independent trials (e.g. multiple coin tosses) often follow the <strong>binomial distribution</strong>, 
              modeling exactly <em>k</em> successes in <em>n</em> trials with success probability <em>p</em>:
            </p>
            <pre style={{ background: "#f5f5f5", padding: "6px" }}>
    {`P(X=k) = C(n, k) * p^k * (1 - p)^(n - k)`}
            </pre>
            <p>
              <strong>Example:</strong> Probability of exactly 3 heads in 5 tosses (p=0.5)=
              <code>C(5,3)* (0.5)^3 * (0.5)^2=10 *0.125 *0.25=0.3125</code>.
            </p>
            <BinomialTool n={5} p={0.5} style={{ maxWidth: 480 }} />
    
            {/* Or ASCII bar chart representation */}
            <pre style={{ background:"#f5f5f5", padding:6 }}>
    {`k:   0    1    2    3    4    5
    P(k)  1/32 5/32 10/32 10/32 5/32 1/32
    (bar heights for each k)`}
            </pre>
    
            <p style={{ marginTop: "1em" }}>
              Other distributions (Poisson, geometric, normal) handle different scenarios (event rates, continuous measures).
            </p>
          </>
        )
      },
    
      // SLIDE 5: Normal Approximation & Real-World Data
      {
        title: <h1>Statistics &amp; Probability — Slide 5: Normal Approximation &amp; Real-World Data</h1>,
        body: (
          <>
            <p>
              Large datasets often approximate the <strong>normal (bell) distribution</strong>. 
              By the Central Limit Theorem, sums or averages of many random variables trend bell-shaped.
            </p>
            <p>
              <strong>Normal Curves:</strong> Centered at mean <em>μ</em>, spread by std dev <em>σ</em>. 
              ~68% of data within μ ± σ, ~95% within μ ± 2σ, etc.
            </p>
            <p>
              <strong>Application:</strong> Adult heights, test scores, many natural phenomena 
              approximate normal. 
            </p>
            <NormalDistributionDemo mean={100} stdDev={15} showCurve />
    
            {/* Or ASCII normal curve */}
            <pre style={{ background:"#f5f5f5", padding:6 }}>
    {`     (peak at mean)
           ___
      ___--     --___
    --               --  ~68% within 1 std dev
    `}
            </pre>
    
            <p style={{ marginTop: "1em" }}>
              Using <strong>z-scores</strong>=(x−μ)/σ helps us compare different normal distributions or find probabilities via z-tables. 
              This is key in advanced inferential stats (confidence intervals, hypothesis tests).
            </p>
          </>
        )
      }
    ],  
  
    // --------------------------------------------
    // CHAPTER 9: Linear Inequalities
    // --------------------------------------------
    // ─── Chapter 9  Linear Inequalities ──────────────────────────────────────────
  9: [
    /* ── Slide 1  One-Variable Inequalities ───────────────────────────── */
    {
      title: <h1>Linear Inequalities — Slide 1: Solving in ℝ</h1>,
      body: (
        <>
          <p>
            A <strong>linear inequality in one variable</strong>
            (e.g.&nbsp;<code>-2x + 5 &gt; 1</code>) is solved almost
            exactly like an equation:
          </p>
  
          <ol>
            <li>Isolate <code>x</code> with inverse operations.</li>
            <li>
              <em>Flip the inequality sign</em> <strong>iff</strong> you multiply
              or divide by a negative number.
            </li>
          </ol>
  
          <pre style={{ background: "#f5f5f5", padding: 6 }}>
  {`-2x + 5  >  1
  -2x       >  -4
  x         <   2   ← flipped (> becomes <)`}
          </pre>
  
          <p>
            Solution set&nbsp;=&nbsp;<code>{`{ x ∈ ℝ | x < 2 }`}</code> (all real
            numbers to the left of 2 on the number line).
          </p>
        </>
      )
    },
  
    /* ── Slide 2  Graphing a Half-Plane ───────────────────────────────── */
    {
      title: <h1>Linear Inequalities — Slide 2: Graphing in 2D</h1>,
      body: (
        <>
          <p>
            For&nbsp;<code>y &gt; 2x + 1</code>:
            <ol>
              <li>
                Graph the <em>boundary line</em>&nbsp;
                <code>y = 2x + 1</code> (dashed, because &gt; not ≥).
              </li>
              <li>
                Pick a test point not on the line (e.g.&nbsp;<code>(0,0)</code>).
                If it satisfies the inequality, shade that side.
              </li>
            </ol>
          </p>
  
          {/* Illustration — uses your existing CoordinatePlane.js */}
          <LinearInequalityPlot m={2} b={1} op=">" />
  
          <p style={{ fontSize: "1em" }}>
            The test point&nbsp;(0,0) gives&nbsp;0 &gt; 1? False → shade the 
            <em> other</em> side (above the line).
          </p>
        </>
      )
    },
  
    /* ── Slide 3  Systems of Inequalities ─────────────────────────────── */
    {
      title: <h1>Linear Inequalities — Slide 3: Systems &amp; Feasible Regions</h1>,
      body: (
        <>
          <p>
            A <strong>system</strong> like
            <code>{`  { y ≥ x + 1,   y ≤ -x + 5 }`}</code>
            defines two half-planes.  Their <em>intersection</em> is the
            <strong> feasible region</strong>.
          </p>
          <InequalitySystemPlot
          inequalities={[
            { type: ">=", m: 1, b: 1, color: "#4caf50" },
            { type: "<=", m: -1, b: 5, color: "#f44336" },
          ]}
          xDomain={[-1, 10]} // pick a suitable range to show the intersection
          yDomain={[-1, 10]}
          width={350}
          height={280}
        />
        
          <p style={{ fontSize: "1em" }}>
            Any point inside the grey triangle satisfies <em>both</em> inequalities.
          </p>
        </>
      )
    },
  
    /* ── Slide 4  Optimisation Context ────────────────────────────────── */
    {
      title: <h1>Linear Inequalities — Slide 4: Optimizing over a Region</h1>,
      body: (
        <>
          <p>
            In <strong>linear programming</strong> we optimize a linear objective
            (e.g.&nbsp;<code>z = 3x + 2y</code>) subject to a system of
            inequalities. The optimum always occurs at a <em>vertex</em> of
            the feasible region.
          </p>
    
          {/* -- Insert a small feasible region plot -- */}
          <InequalitySystemPlot
            inequalities={[
              // y >= 0 => can be expressed as "y >= 0*x + 0"
              { type: ">=", m: 0, b: 0, color: "#4caf50" },
              // x >= 0 is a vertical boundary (not directly y <= ...),
              // so in code we might handle it as a custom "clip" or bounding box
              // or skip it if we rely on xDomain >= 0. 
              // For demonstration, let's keep it simple and rely on xDomain >= 0.
    
              // y <= -x + 6 => type: "<=", m: -1, b: 6
              { type: "<=", m: -1, b: 6, color: "#f44336" },
              // y <= 4 => treat as "y <= 4" => type: "<=", slope=0, intercept=4
              { type: "<=", m: 0, b: 4, color: "#2196f3" }
            ]}
            xDomain={[0, 7]}  // set x>=0 domain
            yDomain={[0, 7]}  // set y>=0 domain
            width={340}
            height={280}
          />
    
          <ul>
            <li>List the corner points: (0,0), (0,4), (2,4), and (6,0).</li>
            <li>Evaluate <code>z</code> at each corner.</li>
            <li>Pick the best (max or min).</li>
          </ul>
          <p>
          We see that <code>z = 18</code> at <code>D = (6,0)</code> is the largest value,
          so the maximum occurs at that corner.
        </p>
    
          <p style={{ fontSize: "0.85em" }}>
            Hint for students: Connect this to calculus—extrema often occur at
            boundaries when interior derivatives vanish. For linear problems,
            corners (vertices) of the feasible polygon give the optimal value.
          </p>
        </>
      )
    },
  
    /* ── Slide 5  Quick-Check Questions ───────────────────────────────── */
    {
      title: <h1>Linear Inequalities — Slide 5: Check Your Understanding</h1>,
      body: (
        <>
          <ol style={{ lineHeight: "1.6" }}>
            <li>
              Solve <code>5 - 3x ≤ 11</code> and express the solution on a number line.
            </li>
            
            <li>
              Without graphing, determine whether the point <code>(4, 2)</code> satisfies
              <code>y &lt; -½x + 4</code>.
            </li>
            <li>
              Describe the feasible region for <code>{"{ x ≥ 0,  y ≥ 0,  x + y ≤ 6 }"}</code>.
            </li>
          </ol>
          <p style={{ fontSize: "1.2em" }}>
            Answers on the next slide — no peeking!
          </p>
        </>
      )
    },
  
    /* ── Slide 6  Answers ─────────────────────────────────────────────── */
    {
      title: <h1>Linear Inequalities — Slide 6: Solutions</h1>,
      body: (
        <>
          <ol>
            <li>
              <code>{`x ≥ -2`}</code>.&nbsp;Number line: solid dot at −2,
              arrow to +∞.
            </li>
            <CoordinatePlane
    width={300}
    height={200}
    xDomain={[-5, 5]}
    yDomain={[-0.5, 0.5]}  // squashes the vertical range
    showGrid={false}
    showAxes={true}
    axisMode="border"
    disablePlot={true}
    lines={[
      {
        // We'll draw a short vertical line from (-2, -0.4) to (-2, +0.4)
        start: [-2, -0.4],
        end:   [-2,  0.4],
        color: "blue"
      }
    ]}
  />
            <li>
              Substitute → 2&nbsp;&lt;&nbsp;−½(4)+4&nbsp;= 2  ⇒ 
              <strong>False</strong>; the point is <em>not</em> in the region.
            </li>
            <CoordinatePlane
    width={300}
    height={200}
    xDomain={[0, 8]}
    yDomain={[0, 6]}
    showGrid={false}
    showAxes={true}
    axisMode="border"
    disablePlot={true}
    lines={[
      {
        // If x=0 => y=4; If x=8 => y=0
        start: [0, 4],
        end:   [8, 0],
        color: "red"
      }
    ]}
  />
            <li>
              A right triangle with vertices (0,0), (6,0) and (0,6).
            </li>
            <CoordinatePlane
    width={300}
    height={200}
    xDomain={[-1, 7]}
    yDomain={[-1, 7]}
    showGrid={false}
    showAxes={true}
    axisMode="border"
    disablePlot={true}
    lines={[
      // x=0 can appear as a vertical line from (0, -1) to (0, 7)
      {
        start: [0, -1],
        end:   [0,  7],
        color: "blue"
      },
      // y=0 => horizontal line from (-1,0) to (7,0)
      {
        start: [-1, 0],
        end:   [ 7, 0],
        color: "grey"
      },
      // x+y=6 => y=-x+6, from x=0 => y=6, to x=6 => y=0
      {
        start: [0, 6],
        end:   [6, 0],
        color: "red"
      }
    ]}
  />
          </ol>
        </>
      )
    }
  ],
  
  
    // --------------------------------------------
    // CHAPTER 10: Function Notation & Transformations
    // --------------------------------------------
    10: [
      {
        title: <h1>Function Notation — Slide 1</h1>,
        body: (
          <>
            <p>
              <strong>Function Notation.</strong> If <code>y = f(x)</code>, then <code>f(a)</code> is the
              output when <code>x = a</code>. In other words, we plug <code>a</code> into
              the function <code>f</code>.
            </p>
            <ul>
              <li><code>f(x) = x<sup>2</sup></code> ⇒ <code>f(2) = 4</code>, <code>f(-3) = 9</code></li>
              <li><code>f(x) = 3x + 1</code> ⇒ <code>f(5) = 16</code>, etc.</li>
            </ul>
            <p style={{ fontSize: "0.9em" }}>
              <em>Tip:</em> “<code>f</code>” is just a name. You could also see 
              <code>g(x), h(t), p(q)</code>—they all represent a rule that transforms inputs to outputs.
            </p>
          </>
        )
      },
      {
        title: <h1>Transformations — Slide 2</h1>,
        body: (
          <>
            <p>
              <strong>Vertical &amp; Horizontal Shifts.</strong> For any base function <code>f(x)</code>:
            </p>
            <ul>
              <li>
                <code>f(x) + k</code> shifts the entire graph <em>up</em> (if k &gt; 0) 
                or <em>down</em> (if k &lt; 0).
              </li>
              <li>
                <code>f(x - h)</code> shifts the entire graph <em>to the right</em> (if h &gt; 0) 
                or <em>to the left</em> (if h &lt; 0).
              </li>
            </ul>
            <p>
              <em>Example:</em> If <code>f(x) = x<sup>2</sup></code>, then:
            </p>
            <ul>
              <li><code>x<sup>2</sup> + 3</code> is a vertical shift up by 3.</li>
              <li><code>(x - 2)<sup>2</sup></code> is a shift right by 2.</li>
            </ul>
          </>
        )
      },
      {
        title: <h1>Transformations — Slide 3</h1>,
        body: (
          <>
            <p>
              <strong>Reflections &amp; Stretches.</strong> For a base function <code>f(x)</code>:
            </p>
            <ul>
              <li><code>-f(x)</code> reflects the graph across the x-axis.</li>
              <li><code>f(-x)</code> reflects the graph across the y-axis.</li>
              <li><code>a·f(x)</code> applies a <em>vertical stretch</em> by factor <code>|a|</code> 
                (if <code>a &gt; 1</code>), or a compression if <code>0 &lt; a &lt; 1</code>.
              </li>
            </ul>
            <p>
              <em>Example:</em> If <code>f(x) = x<sup>2</sup></code>, 
              then <code>-x<sup>2</sup></code> flips it upside down 
              and <code>2x<sup>2</sup></code> stretches it taller.
            </p>
          </>
        )
      },
      {
        title: <h1>Function Notation & Transformations — Slide 4</h1>,
        body: (
          <>
            <p>
              That’s the gist of these transformations—vertical and horizontal shifts,
              reflections, and stretches/compressions. You’ll apply these ideas to
              various base functions:
            </p>
            <ul>
              <li>Polynomials (e.g. <code>x<sup>2</sup></code>, <code>x<sup>3</sup></code>)</li>
              <li>Radicals (e.g. <code>√x</code>)</li>
              <li>Rational functions (e.g. <code>1/x</code>)</li>
              <li>Exponentials (e.g. <code>2<sup>x</sup></code>)</li>
              <li>Trigonometric (e.g. <code>sin(x)</code>, <code>cos(x)</code>)</li>
            </ul>
            <p style={{ fontSize: "0.9em" }}>
              <em>Key takeaway:</em> Each transformation modifies the base shape in a
              predictable way—shift, reflect, or stretch.
            </p>
          </>
        )
      }    
    ]
  };